// A switch's settings beside its ports' own fields (ZENITH.md §11.3): its voice
// VLAN (ADR-0369 decision 1a), its IGMP snooping, for the switch and per
// VLAN (ADR-0373 decision 1a), each port's spanning-tree edge port (ADR-0426
// decision 3a), its spanning-tree globals (ADR-0426 decision 2a, as ADR-0445
// amends it), and its remote syslog and own name servers (ADR-0444), which
// were command-line settings until that ruling. Shown wherever the switch's port read answers
// them, and edited on Design › Wired's Ports tab, where the page hands this the
// port write.
//
// **What is shown is what the switch's last port import found, and what is
// edited is intent beside it.** A field left at *keep* states no intent, and the
// plan sends that field as the controller holds it. Saving is the ordinary
// `PUT /switches/{serial}/ports`, every port sent back as read, so nothing here
// reaches a controller: the operator plans and applies afterwards.
//
// **The server decides what may change.** A setting the corpus does not attest
// for the switch's model is not in `editable_settings`, and the plan refuses an
// edit of it by name. Its control is drawn disabled with the reason beside it,
// rather than offered and refused at Plan.
import { useMemo, useState } from "react";
import type { components } from "../../api/schema";
import type { useMutation } from "../../api/mutation";
import { Card, Info, ProblemBox } from "../../ui";
import { PortName, byNumber } from "./DeviceFace";
import { entryOf } from "./PortsEditor";
import { unrecognized } from "../../api/openEnum";

type PortSet = components["schemas"]["PortIntentSet"];
type Entry = components["schemas"]["PortIntentEntry"];
type Request = components["schemas"]["PortIntentSetRequest"];
type VoiceMode = components["schemas"]["PortVoiceIntentMode"];
type Detect = components["schemas"]["PortVoiceIntentDetectVoice"];

/** The schema paths `editable_settings` names. */
export const VOICE_PATHS = {
  vlan: "switch.voice-vlan-config.voice-vlan-id",
  mode: "ethernet[].voice-vlan-intf-config.voice-vlan-intf-mode",
  priority: "ethernet[].voice-vlan-intf-config.voice-vlan-intf-priority",
  detect: "ethernet[].voice-vlan-intf-config.voice-vlan-intf-detect-voice",
} as const;

/** One port's voice form: "" keeps what the controller holds. */
export interface PortVoiceForm { mode: string; priority: string; detect: string }
export interface VoiceForm { vlan: string; ports: Record<string, PortVoiceForm> }

/** The form as the read says the intent is now. */
export function voiceFormOf(set: PortSet): VoiceForm {
  const ports: Record<string, PortVoiceForm> = {};
  for (const p of set.ports) {
    if (!p.voice) continue;
    const i = p.voice.intent;
    ports[p.port] = { mode: i?.mode ?? "", priority: i?.priority === undefined ? "" : String(i.priority), detect: i?.detect_voice ?? "" };
  }
  return { vlan: set.voice?.intent?.vlan_id === undefined ? "" : String(set.voice.intent.vlan_id), ports };
}

/** The request a save sends: every port as read, a port's voice where its form
 * moved from the intent the read gave, and the switch's id where it moved. A
 * port whose form did not move carries no `voice`, which leaves its intent as
 * it is. Pure, so a test can hold the request against the form. */
export function voiceRequest(set: PortSet, form: VoiceForm): { ports: Entry[]; voice?: Request["voice"] } {
  const was = voiceFormOf(set);
  const ports = set.ports.map((p) => {
    const e = entryOf(p);
    const f = form.ports[p.port], w = was.ports[p.port];
    if (!f || !w || (f.mode === w.mode && f.priority === w.priority && f.detect === w.detect)) return e;
    return {
      ...e,
      voice: {
        ...(f.mode ? { mode: f.mode as VoiceMode } : {}),
        ...(f.priority !== "" ? { priority: Number(f.priority) } : {}),
        ...(f.detect ? { detect_voice: f.detect as Detect } : {}),
      },
    };
  }).sort((a, b) => byNumber(a.port, b.port));
  if (form.vlan === was.vlan) return { ports };
  return { ports, voice: form.vlan === "" ? {} : { vlan_id: Number(form.vlan) } };
}

const KEEP = "keep as held";
const NO_INTENT: PortVoiceForm = { mode: "", priority: "", detect: "" };
// Restates ADR-0369 decision 4 and ZENITH.md §11.3 — what Zenith sends a switch stays inside what the
// corpus attests, so a setting no corpus configuration of the model carries is not changed.
const UNATTESTED = "This is not changed on this switch's model yet: no configuration in the corpus for the model carries it.";
// Restates ADR-0426 decision 6a — a spanning-tree setting is offered only on a model and build a lab run
// measured taking it.
const UNMEASURED = "This is not changed on this switch\u2019s model and build yet: no lab run has measured it taking this.";

interface SettingsProps {
  set: PortSet;
  /** The save's lane, so its refusal is said here; absent where the page only reads. */
  lane?: ReturnType<typeof useMutation>;
  onSave?(body: Request): Promise<boolean>;
}

/** The form state both sections start from, for the page's key: a save's
 * reload starts the forms from what was stored. */
export const settingsKey = (set: PortSet): string =>
  JSON.stringify([voiceFormOf(set), snoopingFormOf(set), edgeFormOf(set), globalsFormOf(set), remoteLogFormOf(set), nameServersFormOf(set)]);

export default function SwitchSettings(props: SettingsProps) {
  return (
    <>
      <VoiceVlan {...props} />
      <IgmpSnooping {...props} />
      <EdgePorts {...props} />
      <SpanningTree {...props} />
      <RemoteLog {...props} />
      <NameServers {...props} />
    </>
  );
}

function VoiceVlan({ set, lane, onSave }: SettingsProps) {
  const voicePorts = useMemo(() => set.ports.filter((p) => p.voice).sort((a, b) => byNumber(a.port, b.port)), [set]);
  const [form, setForm] = useState<VoiceForm>(() => voiceFormOf(set));
  if (!set.voice && voicePorts.length === 0) return null;
  const editable = new Set(set.editable_settings ?? []);
  const may = (path: string) => onSave !== undefined && editable.has(path);
  const anyEditable = Object.values(VOICE_PATHS).some(may);
  const setPort = (port: string, field: keyof PortVoiceForm, value: string) =>
    setForm((f) => ({ ...f, ports: { ...f.ports, [port]: { ...(f.ports[port] ?? NO_INTENT), [field]: value } } }));
  const req = voiceRequest(set, form);
  const moved = req.voice !== undefined || req.ports.some((e) => e.voice !== undefined);
  return (
    <Card className="panel" title="Voice VLAN"
      head={<Info label="About the voice VLAN">
        {/* Restates ADR-0369 decision 1a — the voice VLAN id and each port's mode, priority and detection
            are editable, the ageing time and a port's security are kept as found, and a field left
            unset is sent as the controller holds it. */}
        As the switch&rsquo;s last port import found it. A plan sends the voice VLAN as the controller holds it,
        except a field set here. The aging time and each port&rsquo;s security are kept as found.
      </Info>}>
      {onSave && !anyEditable && <p className="muted" role="note">{UNATTESTED}</p>}
      <dl className="kv">
        <dt>Voice VLAN</dt>
        <dd>
          {set.voice?.vlan_id ?? <span className="muted">not stated</span>}
          {set.voice?.ageing_time !== undefined && <>; aging time {set.voice.ageing_time} min</>}
          {(set.voice?.oui_entries ?? 0) > 0 && <>; {set.voice!.oui_entries} OUI entries, kept as found</>}
          {onSave && set.voice && (
            <>
              {" "}
              <label>Set to{" "}
                <input type="number" min={1} max={4094} value={form.vlan} disabled={!may(VOICE_PATHS.vlan)} placeholder={KEEP}
                  aria-label="Voice VLAN to set" onChange={(e) => setForm((f) => ({ ...f, vlan: e.target.value }))} />
              </label>
            </>
          )}
        </dd>
      </dl>
      {voicePorts.length > 0 && (
        <div className="table-wrap"><table aria-label="Voice VLAN by port">
          <thead><tr><th>Port</th><th>Mode</th><th>Priority</th><th>Detection</th><th>Security</th></tr></thead>
          <tbody>{voicePorts.map((p) => {
            const v = p.voice!;
            const f = form.ports[p.port] ?? NO_INTENT;
            return (
              <tr key={p.port}>
                <td><PortName name={p.port} /></td>
                <td>{v.mode ?? <span className="muted">not stated</span>}
                  {onSave && <> <select aria-label={`${p.port} voice mode to set`} value={f.mode} disabled={!may(VOICE_PATHS.mode)} onChange={(e) => setPort(p.port, "mode", e.target.value)}>
                    <option value="">{KEEP}</option><option value="none">none</option><option value="manual">manual</option><option value="auto">auto</option>
                  </select></>}
                </td>
                <td>{v.priority ?? <span className="muted">not stated</span>}
                  {onSave && <> <input type="number" min={0} max={6} aria-label={`${p.port} voice priority to set`} value={f.priority} placeholder={KEEP}
                    disabled={!may(VOICE_PATHS.priority)} onChange={(e) => setPort(p.port, "priority", e.target.value)} /></>}
                </td>
                <td>{v.detect_voice ?? <span className="muted">not stated</span>}
                  {onSave && <> <select aria-label={`${p.port} voice detection to set`} value={f.detect} disabled={!may(VOICE_PATHS.detect)} onChange={(e) => setPort(p.port, "detect", e.target.value)}>
                    <option value="">{KEEP}</option><option value="oui">oui</option><option value="lldp">lldp</option>
                  </select></>}
                </td>
                <td>{v.security === undefined ? <span className="muted">not stated</span> : v.security ? "on" : "off"}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
      )}
      {onSave && anyEditable && (
        <div className="actions">
          <button type="button" className="btn" disabled={!moved || lane?.busy} onClick={() => void onSave(req)}>Save the voice VLAN</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(voiceFormOf(set))}>Reset</button>
        </div>
      )}
      {lane?.problem && <ProblemBox problem={lane.problem} onDismiss={lane.clear} />}
    </Card>
  );
}

/** The snooping form: "" keeps what the controller holds, "on" and "off" set it. */
export interface SnoopingForm { global: string; vlans: Record<number, string> }

const word = (b: boolean | null | undefined): string => (b === true ? "on" : b === false ? "off" : "");

/** The form as the read says the intent is now. */
export function snoopingFormOf(set: PortSet): SnoopingForm {
  const vlans: Record<number, string> = {};
  for (const v of set.vlan_multicast ?? []) vlans[v.vlan_id] = word(v.intent?.snooping_enable);
  return { global: word(set.multicast?.intent?.igmp_snooping_enable), vlans };
}

const bool = (w: string): boolean | null => (w === "on" ? true : w === "off" ? false : null);

/** The request a save sends: every port as read, the switch's snooping where
 * it moved, and each VLAN whose snooping moved. Pure, for the test. */
export function snoopingRequest(set: PortSet, form: SnoopingForm): Request {
  const was = snoopingFormOf(set);
  const out: Request = { ports: set.ports.map(entryOf).sort((a, b) => byNumber(a.port, b.port)) };
  if (form.global !== was.global) out.multicast = { igmp_snooping_enable: bool(form.global) };
  const moved = Object.keys(form.vlans).map(Number).filter((v) => form.vlans[v] !== was.vlans[v]).sort((a, b) => a - b);
  if (moved.length > 0) out.vlan_multicast = moved.map((v) => ({ vlan_id: v, snooping_enable: bool(form.vlans[v] ?? "") }));
  return out;
}

export const SNOOPING_PATHS = {
  global: "unit.multicast.igmp-snooping-enable",
  vlan: "interfaces[].ipv4.multicast.igmp.snooping-enable",
} as const;

const found = (b: boolean | undefined) => (b === undefined ? <span className="muted">not stated</span> : b ? "on" : "off");

function SnoopingSelect({ label, value, disabled, onChange, noOn }: { label: string; value: string; disabled: boolean; onChange(v: string): void; noOn?: boolean }) {
  return (
    <select aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">{KEEP}</option><option value="on" disabled={noOn}>on</option><option value="off">off</option>
    </select>
  );
}

function IgmpSnooping({ set, lane, onSave }: SettingsProps) {
  const [form, setForm] = useState<SnoopingForm>(() => snoopingFormOf(set));
  const vlans = set.vlan_multicast ?? [];
  if (!set.multicast && vlans.length === 0) return null;
  const editable = new Set(set.editable_settings ?? []);
  const may = (path: string) => onSave !== undefined && editable.has(path);
  const anyEditable = Object.values(SNOOPING_PATHS).some(may);
  const req = snoopingRequest(set, form);
  const moved = req.multicast !== undefined || req.vlan_multicast !== undefined;
  return (
    <Card className="panel" title="IGMP snooping"
      head={<Info label="About IGMP snooping">
        {/* Restates ADR-0373 decision 1a — the switch's IGMP snooping and each VLAN's are editable, every
            other IGMP key is kept as found, and a field left unset is sent as the controller holds it. #1990
            (ruling 1 of #1981 note 4082890133): a VLAN's is editable only on a model and build an apply changing
            it was measured on, which is none yet. ADR-0233 and ADR-0321: turning snooping off on a model and
            build measured to lose it asks on the plan. */}
        As the switch&rsquo;s last port import found it. A plan sends IGMP snooping as the controller holds it,
        except what is set here; the version, timers, fast leave, querier and static groups are kept as found. A
        VLAN&rsquo;s snooping can be set only on a switch model and firmware where changing it has been tested,
        and none has been yet. On a switch whose model and build is measured to lose a VLAN&rsquo;s snooping, a plan
        that turns it off asks you to accept that.
      </Info>}>
      {onSave && !anyEditable && <p className="muted" role="note">{UNATTESTED}</p>}
      <dl className="kv">
        <dt>For the switch</dt>
        <dd>
          {found(set.multicast?.igmp_snooping_enable)}
          {onSave && set.multicast && <> <SnoopingSelect label="IGMP snooping for the switch to set" value={form.global}
            disabled={!may(SNOOPING_PATHS.global)} onChange={(v) => setForm((f) => ({ ...f, global: v }))} /></>}
        </dd>
      </dl>
      {vlans.length > 0 && (
        <div className="table-wrap"><table aria-label="IGMP snooping by VLAN">
          <thead><tr><th>VLAN</th><th>Snooping</th><th>Version</th><th>Fast leave</th></tr></thead>
          <tbody>{vlans.map((v) => (
            <tr key={v.vlan_id}>
              <td>{v.vlan_id}</td>
              <td>{found(v.snooping_enable)}
                {onSave && <> <SnoopingSelect label={`VLAN ${v.vlan_id} IGMP snooping to set`} value={form.vlans[v.vlan_id] ?? ""}
                  disabled={!may(SNOOPING_PATHS.vlan)} onChange={(w) => setForm((f) => ({ ...f, vlans: { ...f.vlans, [v.vlan_id]: w } }))} /></>}
              </td>
              <td>{v.version ?? <span className="muted">not stated</span>}</td>
              <td>{found(v.fast_leave_enable)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {onSave && anyEditable && (
        <div className="actions">
          <button type="button" className="btn" disabled={!moved || lane?.busy} onClick={() => void onSave(req)}>Save IGMP snooping</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(snoopingFormOf(set))}>Reset</button>
        </div>
      )}
    </Card>
  );
}

/** The schema path `editable_settings` names for a port's edge port. */
export const EDGE_PORT_PATH = "ethernet[].edge-port";

/** The edge-port form, per port: "" keeps what the controller holds, "on" and "off" set it. */
export type EdgeForm = Record<string, string>;

/** The form as the read says the intent is now. */
export function edgeFormOf(set: PortSet): EdgeForm {
  const out: EdgeForm = {};
  for (const p of set.ports) out[p.port] = word(p.edge_port);
  return out;
}

/** The request a save sends: every port as read, and each port whose edge port
 * moved with it set, or with no `edge_port` at all where the form went back to
 * keep. Pure, for the test. */
export function edgeRequest(set: PortSet, form: EdgeForm): Request {
  const was = edgeFormOf(set);
  const ports = set.ports.map((p) => {
    const e: Entry = entryOf(p);
    const f = form[p.port] ?? "";
    if (f === (was[p.port] ?? "")) return e;
    const { edge_port: _dropped, ...rest } = e;
    void _dropped;
    return f === "" ? rest : { ...rest, edge_port: f === "on" };
  }).sort((a, b) => byNumber(a.port, b.port));
  return { ports };
}

function EdgePorts({ set, lane, onSave }: SettingsProps) {
  const [form, setForm] = useState<EdgeForm>(() => edgeFormOf(set));
  const ports = useMemo(() => [...set.ports].sort((a, b) => byNumber(a.port, b.port)), [set]);
  const editable = onSave !== undefined && (set.editable_settings ?? []).includes(EDGE_PORT_PATH);
  const anyIntent = set.ports.some((p) => p.edge_port !== undefined);
  if (ports.length === 0 || (!editable && !anyIntent)) return null;
  const uplink = new Set(set.uplink_members ?? (set.uplink_port ? [set.uplink_port] : []));
  const req = edgeRequest(set, form);
  const was = edgeFormOf(set);
  const moved = ports.some((p) => (form[p.port] ?? "") !== (was[p.port] ?? ""));
  return (
    <Card className="panel" title="Edge ports"
      head={<Info label="About edge ports">
        {/* Restates ADR-0426 decisions 3a, 5a and 6a — a port's spanning-tree edge port is set through the
            plan; off takes it off by leaving the key out; a plan leaving one on the switch's uplink is refused,
            held or not, until it is set off; and it is offered only on a model and build a lab run measured
            taking it. */}
        A port set here is told an end station is on it, so spanning tree forwards on it at once. Off takes an edge
        port off. A port left at keep is sent as the controller holds it. The port the switch reaches its controller
        through is never made an edge port: a plan that would leave one on it is refused, including one the
        controller already holds, until it is set off here. This is offered only on a model and build measured
        taking it.
      </Info>}>
      {onSave && !editable && <p className="muted" role="note">{UNMEASURED}</p>}
      <div className="table-wrap"><table aria-label="Edge port by port">
        <thead><tr><th>Port</th><th>Edge port</th></tr></thead>
        <tbody>{ports.map((p) => {
          const up = uplink.has(p.port);
          return (
            <tr key={p.port}>
              <td><PortName name={p.port} />{up && <span className="muted"> (uplink)</span>}</td>
              <td>{p.edge_port === undefined ? <span className="muted">keep as held</span> : p.edge_port ? "on" : "off"}
                {onSave && editable && <> <SnoopingSelect label={`${p.port} edge port to set`} value={form[p.port] ?? ""}
                  disabled={false} noOn={up} onChange={(w) => setForm((f) => ({ ...f, [p.port]: w }))} /></>}
              </td>
            </tr>
          );
        })}</tbody>
      </table></div>
      {onSave && editable && (
        <div className="actions">
          <button type="button" className="btn" disabled={!moved || lane?.busy} onClick={() => void onSave(req)}>Save edge ports</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(edgeFormOf(set))}>Reset</button>
        </div>
      )}
    </Card>
  );
}

/** The schema paths `editable_settings` names for the spanning-tree globals. */
export const GLOBALS_PATHS = {
  enabled: "switch.loop-detection.instances[].enabled",
  priority: "switch.loop-detection.instances[].priority",
} as const;

/** The bridge priorities offered: 0 to 61440 in steps of 4096, the whole set
 * the server admits. One below 32768 can move the root bridge, so the plan that
 * sets it asks for its acceptance; the server decides that, not this list. */
export const GLOBALS_PRIORITIES: readonly number[] = Array.from({ length: 16 }, (_, n) => n * 4096);

/** The globals form: "" keeps what the controller holds, a number turns
 * spanning tree on at that bridge priority. */
export type GlobalsForm = string;

/** The form as the read says the intent is now. */
export function globalsFormOf(set: PortSet): GlobalsForm {
  const p = set.loop_detection?.priority;
  return set.loop_detection?.enabled && p !== undefined ? String(p) : "";
}

/** The request a save sends: every port as read, and the globals set, or `{}`
 * where the form went back to keep. Pure, for the test. */
export function globalsRequest(set: PortSet, form: GlobalsForm): Request {
  return {
    ports: set.ports.map(entryOf).sort((a, b) => byNumber(a.port, b.port)),
    loop_detection: form === "" ? {} : { enabled: true, priority: Number(form) },
  };
}

function SpanningTree({ set, lane, onSave }: SettingsProps) {
  const [form, setForm] = useState<GlobalsForm>(() => globalsFormOf(set));
  const editable = onSave !== undefined && Object.values(GLOBALS_PATHS).every((p) => (set.editable_settings ?? []).includes(p));
  if (!editable && !set.loop_detection) return null;
  const moved = form !== globalsFormOf(set);
  return (
    <Card className="panel" title="Spanning tree"
      head={<Info label="About spanning tree">
        {/* Restates ADR-0445, amending ADR-0426 decision 2a — the globals are sent as RSTP on at a bridge priority,
            the priority always and no timer, so the switch keeps its own timers; turning spanning tree off is not
            offered (ADR-0426 decision 1a), so keep leaves a bridge these turned on as the controller holds it; every
            priority of decision 9a's set is offered, and one below 32768 is asked for at the plan's confirmation, not
            here; and it is offered only on the one model and build measured. The window is unmeasured (ADR-0445). */}
        Set here, a plan turns rapid spanning tree on at the bridge priority you choose. The switch keeps its own
        hello time, max age and forward delay. Keep sends spanning tree as the controller holds it. Spanning tree is
        not turned off from here: once a plan has turned it on, keep leaves it on. This is offered only on a model
        and build measured taking it.
      </Info>}>
      {/* What turning it on costs the site, so it stays in the open beside the act (#1900, web/COPY.md
          *"Learn more" and the ⓘ*); the marker above the ⓘ covers it with the rest. */}
      <p className="hint">Turning it on holds every linked port out of forwarding while it settles, the uplink included, for a time nobody has measured.</p>
      {onSave && !editable && <p className="muted" role="note">{UNMEASURED}</p>}
      <dl className="kv">
        <dt>Set to</dt>
        <dd>
          {set.loop_detection?.enabled && set.loop_detection.priority !== undefined
            ? <>on, bridge priority {set.loop_detection.priority}</>
            : <span className="muted">keep as held</span>}
          {onSave && editable && (
            <>
              {" "}
              <select aria-label="Spanning tree to set" value={form} onChange={(e) => setForm(e.target.value)}>
                <option value="">{KEEP}</option>
                {GLOBALS_PRIORITIES.map((p) => <option key={p} value={String(p)}>on, bridge priority {p}</option>)}
              </select>
            </>
          )}
        </dd>
      </dl>
      {onSave && editable && (
        <div className="actions">
          <button type="button" className="btn" disabled={!moved || lane?.busy} onClick={() => void onSave(globalsRequest(set, form))}>Save spanning tree</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(globalsFormOf(set))}>Reset</button>
        </div>
      )}
    </Card>
  );
}

/** The schema paths `editable_settings` names for a remote syslog and the name servers (ADR-0444). */
export const SERVICES_PATHS = { log: "services.log", dns: "switch.dns" } as const;

/** "" keeps; `stated` and `removed` are the two a form sends. Any other is a state the read gave that
 * this build does not know (ADR-0511): it is held as received, shown unrecognized and never sent,
 * because a form left as read sends nothing. */
type LogState = string;

/** The remote-syslog form: state "" keeps what the controller holds. */
export interface RemoteLogForm { state: LogState; host: string; port: string; priority: string }

/** The form as the read says the intent is now. */
export function remoteLogFormOf(set: PortSet): RemoteLogForm {
  const i = set.remote_log?.intent;
  if (!i || i.state === "keep") return { state: "", host: "", port: "", priority: "" };
  return { state: i.state, host: i.host ?? "", port: i.port === undefined ? "" : String(i.port), priority: i.priority === undefined ? "" : String(i.priority) };
}

/** A dotted-quad IPv4; the server refuses what cannot be a host and says so. */
const IPV4 = /^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

/** Whether a stated form can be sent: a host, a port in range or none (514), a priority 0..7. */
export function remoteLogFormValid(f: RemoteLogForm): boolean {
  if (f.state !== "stated") return true;
  const port = f.port === "" ? 514 : Number(f.port);
  return IPV4.test(f.host) && Number.isInteger(port) && port >= 100 && port <= 65535 && /^[0-7]$/.test(f.priority);
}

/** The request a save sends: every port as read, and `remote_log` where the form moved. Pure, for the test. */
export function remoteLogRequest(set: PortSet, form: RemoteLogForm): Request {
  const out: Request = { ports: set.ports.map(entryOf).sort((a, b) => byNumber(a.port, b.port)) };
  if (JSON.stringify(form) === JSON.stringify(remoteLogFormOf(set))) return out;
  if (form.state === "") out.remote_log = { state: "keep" };
  else if (form.state === "removed") out.remote_log = { state: "removed" };
  else out.remote_log = { state: "stated", host: form.host, priority: Number(form.priority), ...(form.port !== "" ? { port: Number(form.port) } : {}) };
  return out;
}

const SEVERITY = ["emergencies", "alerts", "critical", "errors", "warnings", "notifications", "informational", "debugging"];

function RemoteLog({ set, lane, onSave }: SettingsProps) {
  const [form, setForm] = useState<RemoteLogForm>(() => remoteLogFormOf(set));
  const editable = onSave !== undefined && (set.editable_settings ?? []).includes(SERVICES_PATHS.log);
  if (!editable && !set.remote_log) return null;
  const held = set.remote_log?.held;
  const req = remoteLogRequest(set, form);
  const moved = req.remote_log !== undefined;
  return (
    <Card className="panel" title="Remote syslog"
      head={<Info label="About remote syslog">
        {/* Restates ADR-0444 decisions 2, 3 and 5 — a switch's remote syslog is sent in its configuration: stated,
            it turns remote logging on with the host, port and level; removed, it turns remote logging off and the
            switch keeps the last level; it is offered only on a model and build a lab run measured taking it. */}
        Sent in the switch&rsquo;s configuration by the plan. A server set here turns remote logging on, sending
        messages up to the level chosen. Off turns remote logging off; the switch keeps the last level. Keep sends
        what the controller holds. This is offered only on a model and build Zenith has measured taking it.
      </Info>}>
      {onSave && !editable && <p className="muted" role="note">Zenith does not change this on this switch&rsquo;s model and build yet.</p>}
      <dl className="kv">
        <dt>In the configuration the controller holds</dt>
        <dd>{held?.host ? <>{held.host} port {held.port ?? <span className="muted">not stated</span>}, level {held.priority ?? <span className="muted">not stated</span>}</> : <span className="muted">none</span>}</dd>
      </dl>
      {onSave && editable && (
        <div className="actions">
          <label>Remote syslog{" "}
            <select aria-label="Remote syslog to set" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value as LogState }))}>
              <option value="">{KEEP}</option><option value="stated">send to a server</option><option value="removed">off</option>
              {!["", "stated", "removed"].includes(form.state) && <option value={form.state}>{unrecognized(form.state)}</option>}
            </select>
          </label>
          {form.state === "stated" && (
            <>
              {" "}<label>Server{" "}<input type="text" inputMode="decimal" maxLength={15} placeholder="192.0.2.10" aria-label="Syslog server address"
                value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value.trim() }))} /></label>
              {" "}<label>Port{" "}<input type="number" min={100} max={65535} placeholder="514" aria-label="Syslog server port"
                value={form.port} onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))} /></label>
              {" "}<label>Level{" "}
                <select aria-label="Syslog level" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                  <option value="">choose</option>
                  {SEVERITY.map((w, n) => <option key={n} value={String(n)}>{n} {w}</option>)}
                </select>
              </label>
            </>
          )}
          {" "}<button type="button" className="btn" disabled={!moved || !remoteLogFormValid(form) || lane?.busy} onClick={() => void onSave(req)}>Save remote syslog</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(remoteLogFormOf(set))}>Reset</button>
        </div>
      )}
    </Card>
  );
}

/** As `LogState`. */
type DnsState = string;

/** The name-server form: state "" keeps what the controller holds; servers are typed one per field. */
export interface NameServersForm { state: DnsState; servers: string[] }

/** The most name servers a stated list holds (ADR-0444 decision 4); the server refuses more. */
export const MAX_NAME_SERVERS = 3;

export function nameServersFormOf(set: PortSet): NameServersForm {
  const i = set.name_servers?.intent;
  if (!i || i.state === "keep") return { state: "", servers: [] };
  return { state: i.state, servers: [...(i.servers ?? [])] };
}

/** The servers a stated form sends: the filled fields, in order. */
const filled = (f: NameServersForm) => f.servers.filter((s) => s !== "");

export function nameServersFormValid(f: NameServersForm): boolean {
  if (f.state !== "stated") return true;
  const list = filled(f);
  return list.length >= 1 && list.length <= MAX_NAME_SERVERS && list.every((s) => IPV4.test(s)) && new Set(list).size === list.length;
}

/** The request a save sends: every port as read, and `name_servers` where the form moved. Pure, for the test. */
export function nameServersRequest(set: PortSet, form: NameServersForm): Request {
  const out: Request = { ports: set.ports.map(entryOf).sort((a, b) => byNumber(a.port, b.port)) };
  const was = nameServersFormOf(set);
  if (form.state === was.state && JSON.stringify(filled(form)) === JSON.stringify(filled(was))) return out;
  if (form.state === "") out.name_servers = { state: "keep" };
  else if (form.state === "removed") out.name_servers = { state: "removed" };
  else out.name_servers = { state: "stated", servers: filled(form) };
  return out;
}

function NameServers({ set, lane, onSave }: SettingsProps) {
  const [form, setForm] = useState<NameServersForm>(() => nameServersFormOf(set));
  const editable = onSave !== undefined && (set.editable_settings ?? []).includes(SERVICES_PATHS.dns);
  if (!editable && !set.name_servers) return null;
  const held = set.name_servers?.held;
  const req = nameServersRequest(set, form);
  const moved = req.name_servers !== undefined;
  const fields = Array.from({ length: MAX_NAME_SERVERS }, (_, n) => form.servers[n] ?? "");
  return (
    <Card className="panel" title="Name servers"
      head={<Info label="About name servers">
        {/* Restates ADR-0444 decisions 4, 5 and 6 — the switch's own name servers are sent in its configuration as
            a whole list in order, at most three, which replaces every name server the switch has, one added at its
            console included; off stops sending the list and the switch keeps what it has; Zenith reads no running list. */}
        The switch&rsquo;s own resolver, sent in its configuration by the plan, at most three in order. Off stops
        sending a list; the switch keeps the name servers it has. Keep sends what the controller holds.
      </Info>}>
      {/* What a list set here loses, so it stays in the open beside the act (#1900, web/COPY.md
          *"Learn more" and the ⓘ*); the marker above the ⓘ covers it with the rest. */}
      <p className="hint">A list set here replaces every name server the switch has, including one added at its console, which Zenith does not read.</p>
      {onSave && !editable && <p className="muted" role="note">Zenith does not change this on this switch&rsquo;s model and build yet.</p>}
      <dl className="kv">
        <dt>In the configuration the controller holds</dt>
        <dd>{held && held.length > 0 ? held.join(", ") : <span className="muted">none</span>}</dd>
      </dl>
      {onSave && editable && (
        <div className="actions">
          <label>Name servers{" "}
            <select aria-label="Name servers to set" value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value as DnsState }))}>
              <option value="">{KEEP}</option><option value="stated">set the list</option><option value="removed">off</option>
              {!["", "stated", "removed"].includes(form.state) && <option value={form.state}>{unrecognized(form.state)}</option>}
            </select>
          </label>
          {form.state === "stated" && fields.map((v, n) => (
            <span key={n}>{" "}<input type="text" inputMode="decimal" maxLength={15} placeholder={n === 0 ? "192.0.2.53" : "optional"}
              aria-label={`Name server ${n + 1}`} value={v}
              onChange={(e) => setForm((f) => { const s = [...fields]; s[n] = e.target.value.trim(); return { ...f, servers: s }; })} /></span>
          ))}
          {" "}<button type="button" className="btn" disabled={!moved || !nameServersFormValid(form) || lane?.busy} onClick={() => void onSave(req)}>Save name servers</button>
          <button type="button" className="btn btn-quiet" disabled={!moved} onClick={() => setForm(nameServersFormOf(set))}>Reset</button>
        </div>
      )}
    </Card>
  );
}
